/**
 * A `<tr>` that navigates when clicked. Handles keyboard (Enter),
 * Cmd/Ctrl+click (new tab), accessibility attributes, and cursor styling.
 *
 * When `to` is set, clicking navigates there.
 * When `to` is not set but `onRowClick` is provided, clicking calls the callback.
 *
 * Children that call `e.stopPropagation()` (such as `<Link>` and `<Button>`)
 * prevent the row click from firing, so they can handle their own navigation.
 *
 * @param {{ to?: string | undefined, navigate?: (path: string) => void, onRowClick?: () => void, children: import('react').ReactNode, [key: string]: any }} props
 * @returns {JSX.Element}
 */
export function ClickableRow({ to, navigate, onRowClick, children, ...props }) {
	return (
		<tr
			onClick={(e) => {
				if (to) {
					if (e.metaKey || e.ctrlKey) {
						window.open(to, '_blank', 'noopener,noreferrer');
					} else if (e.button === 0) {
						navigate(to);
					}
				} else if (onRowClick) {
					onRowClick();
				}
			}}
			onKeyDown={(e) => {
				if (e.key === 'Enter' && to) {navigate?.(to);}
			}}
			tabIndex={to ? 0 : undefined}
			role={to ? 'link' : undefined}
			style={to ? { cursor: 'pointer' } : undefined}
			{...props}
		>
			{children}
		</tr>
	);
}
